import { motion } from "framer-motion";
import { Phone, Clock, ArrowUpRight } from "lucide-react";
import { BUSINESS, IMAGES } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const LINES = ["Breakfast.", "Lunch.", "Dinner."];

const lineV = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.1, delay: 0.3 + i * 0.18, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Hero() {
  return (
    <section id="hero" className="relative min-h-screen w-full overflow-hidden grain">
      <motion.img
        src={IMAGES.hero} alt="Fire-grilled steaks over open flame at Sero's"
        initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease: "easeOut" }}
        style={{ objectPosition: "center 38%" }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/45 to-charcoal/60" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 min-h-screen flex flex-col justify-end pb-28 pt-32">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="text-cream/70 text-xs uppercase tracking-[0.3em] mb-6">
          {BUSINESS.tagline} · Madison Heights, MI
        </motion.p>

        <h1 className="font-serif-display text-cream leading-[0.82] tracking-tight text-6xl sm:text-7xl lg:text-[8.5rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span custom={i} variants={lineV} initial="hidden" animate="show" className="block">
                {i === LINES.length - 1 ? <em className="text-terracotta not-italic">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
          className="mt-8 max-w-xl text-cream/85 text-base md:text-lg leading-relaxed">
          Family owned since day one — hearty breakfasts, classic lunches, and home-cooked
          dinners made fresh to order. Big portions, warm service, and flavor that keeps Madison Heights coming back.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-10 flex flex-wrap gap-3">
          <button onClick={() => scrollToId("order")} data-testid="hero-order-btn"
            className="group inline-flex items-center gap-2 bg-terracotta text-cream px-8 py-4 rounded-full text-sm hover:bg-cream hover:text-ink transition-colors duration-300 active:scale-95">
            Order Online <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
          </button>
          <button onClick={() => scrollToId("catering")} data-testid="hero-catering-btn"
            className="inline-flex items-center gap-2 border border-cream/40 text-cream px-8 py-4 rounded-full text-sm hover:bg-cream/10 transition-colors duration-300 active:scale-95">
            Book Catering
          </button>
          <a href={BUSINESS.phoneHref} data-testid="hero-call-btn"
            className="inline-flex items-center gap-2 border border-cream/40 text-cream px-8 py-4 rounded-full text-sm hover:bg-cream/10 transition-colors duration-300 active:scale-95">
            <Phone size={16} /> Call Now
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 text-cream/80 text-sm border-t border-cream/15 pt-6">
          <span className="flex items-center gap-2"><Phone size={15} className="text-terracotta" /> {BUSINESS.phone}</span>
          <span className="flex items-center gap-2"><Clock size={15} className="text-terracotta" /> Open daily · 7am – 9pm</span>
        </motion.div>
      </div>
    </section>
  );
}
